import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  ErrorDeApi,
  type Cliente,
  type ClienteCreado,
  type EstadoCredencial,
  type FiltrosDeCartera,
  type RegimenFiscal,
  type ResumenDeCliente,
  type TipoPersona,
} from '../servicios/api'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { IconoBuscar, IconoChevron } from '../iconos'
import { PanelDeListado69B } from './PanelDeListado69B'
import { telon } from '../ganchos/telon'

/** El API acota el tamaño de página entre 1 y 200 y por omisión entrega 50. */
const TAMANO_DE_PAGINA = 50

/**
 * Ventana para pedir TODAS las e.firma resguardadas, no solo las que están por vencer: el
 * endpoint filtra por «vence dentro de N días», así que diez años equivalen a «todas».
 * La columna necesita también las que vencen dentro de mucho, no solo las urgentes.
 */
const DIAS_PARA_TRAER_TODAS_LAS_EFIRMAS = 3650

/** C4: por qué los botones de exportación están apagados cuando el filtro no devuelve nada. */
const SIN_NADA_QUE_EXPORTAR = 'Ningún cliente coincide con este filtro: no hay nada que exportar.'

/**
 * Semáforo de la e.firma (RF-204, alertas a 90/30/7 días). Sin credencial resguardada no se puede
 * descargar nada del SAT por ese cliente, así que la ausencia es un aviso, no un guion.
 */
function insigniaDeEfirma(credencial?: EstadoCredencial) {
  if (!credencial) return <span className="insignia aviso">Sin e.firma</span>
  const dias = credencial.diasParaVencer
  if (dias < 0) return <span className="insignia vencido">Vencida</span>
  if (dias <= 7) return <span className="insignia urgente">{dias} d</span>
  if (dias <= 30) return <span className="insignia aviso">{dias} d</span>
  if (dias <= 90) return <span className="insignia suave">{dias} d</span>
  return <span className="insignia vigente">Vigente</span>
}

/** RF-507: cartera limpia también se dice; el silencio no distingue «sin riesgo» de «sin revisar». */
function insigniaDe69B(resumen?: ResumenDeCliente) {
  if (!resumen) return <span className="secundaria">—</span>
  const n = resumen.proveedoresEnRiesgo
  if (n === 0) return <span className="insignia vigente">Limpio</span>
  return <span className="insignia vencido">{n} proveedor{n === 1 ? '' : 'es'}</span>
}

/** Cartera de clientes y alta de contribuyentes. SRS: RF-201, RF-205, RF-206. Cédula: A-01, A-02, A-05, A-06. */
export function Clientes() {
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [total, setTotal] = useState(0)
  const [pagina, setPagina] = useState(1)
  const [busqueda, setBusqueda] = useState('')
  const [tipoPersona, setTipoPersona] = useState<TipoPersona | ''>('')
  // A-01: el despacho trabaja sobre su cartera viva; los archivados se piden a propósito.
  const [estado, setEstado] = useState<'' | 'Activo' | 'Archivado'>('Activo')
  const [regimenFiscal, setRegimenFiscal] = useState('')
  const [regimenes, setRegimenes] = useState<RegimenFiscal[]>([])
  const [error, setError] = useState<string | null>(null)
  const [mostrarAlta, setMostrarAlta] = useState(false)
  // P-92 (RF-206): el alta verifica el RFC contra el listado 69-B; si aparece, se avisa aquí mismo.
  const [alertaDeAlta, setAlertaDeAlta] = useState<ClienteCreado | null>(null)
  // El alta de un RFC archivado terminó en reactivarlo: se dice, porque no se guardó lo capturado.
  const [reactivadoDesdeElAlta, setReactivadoDesdeElAlta] = useState<string | null>(null)
  // Columnas de negocio (C1): el trabajo pendiente de cada cliente, no solo sus datos de registro.
  const [resumenes, setResumenes] = useState<Record<string, ResumenDeCliente>>({})
  const [credenciales, setCredenciales] = useState<Record<string, EstadoCredencial>>({})
  // C4 (RF-705): exportar es una descarga, no una navegación; el botón tiene que decir que
  // está trabajando y el fallo tiene que verse sin tumbar la cartera que ya está en pantalla.
  const [exportando, setExportando] = useState<'excel' | 'pdf' | null>(null)
  const [errorExportar, setErrorExportar] = useState('')

  /**
   * El recorte que el contador tiene puesto. Lo usan el listado y la exportación, y por eso
   * se arma en un solo lugar: el archivo debe ser exactamente lo que se ve en pantalla, y en
   * cuanto los dos lo construyen por su cuenta acaban divergiendo (RF-705).
   */
  const filtrosVigentes = (): FiltrosDeCartera => ({
    busqueda: busqueda || undefined,
    tipoPersona: tipoPersona || undefined,
    estado: estado || undefined,
    regimenFiscal: regimenFiscal || undefined,
  })

  const recargar = () =>
    api
      .listarClientes({ ...filtrosVigentes(), pagina })
      .then((p) => {
        setClientes(p.items)
        setTotal(p.total)
      })
      .catch((e) => setError(e.message))

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, tipoPersona, estado, regimenFiscal, pagina])

  // Cambiar un filtro sin volver a la primera página deja la tabla vacía cuando el resultado
  // nuevo tiene menos páginas que la que estaba abierta.
  useEffect(() => {
    setPagina(1)
  }, [busqueda, tipoPersona, estado, regimenFiscal])

  useEffect(() => {
    api.regimenesFiscales().then(setRegimenes).catch(() => setRegimenes([]))
  }, [])

  // Las dos consultas de la cartera se piden una vez y se cruzan por cliente: ambas son del
  // despacho entero, no dependen del filtro, y fallar aquí no debe tumbar la lista.
  useEffect(() => {
    api.tablero()
      .then((rs) => setResumenes(Object.fromEntries(rs.map((r) => [r.clienteId, r]))))
      .catch(() => setResumenes({}))
    api.credencialesPorVencer(DIAS_PARA_TRAER_TODAS_LAS_EFIRMAS)
      .then((r) => setCredenciales(Object.fromEntries(r.items.map((c) => [c.clienteId, c]))))
      .catch(() => setCredenciales({}))
  }, [])

  /** Exporta la cartera filtrada (C4, RF-705). El servidor manda el archivo; aquí solo se
   *  acompaña la espera y se cuenta el fallo. */
  async function exportar(formato: 'excel' | 'pdf') {
    setExportando(formato)
    setErrorExportar('')
    try {
      await api.exportarClientes(filtrosVigentes(), formato)
    } catch (e) {
      setErrorExportar(e instanceof Error ? e.message : 'No se pudo exportar la cartera.')
    } finally {
      setExportando(null)
    }
  }

  async function reactivar(cliente: Cliente) {
    try {
      await api.reactivarCliente(cliente.id)
      await recargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reactivar el cliente.')
    }
  }


  return (
    <section>
      <header className="encabezado">
        <h1>Clientes</h1>
        <span className="encabezado-sub">
          {estado === 'Archivado' ? `${total} archivados` : `${total} ${estado === 'Activo' ? 'activos' : 'en total'}`}
        </span>
        <span className="encabezado-acciones">
          {/* Sin filas que llevarse el archivo saldría con los encabezados y nada debajo, y
              quien lo abriera no sabría si el filtro no trae nada o si la exportación falló. */}
          <button className="secundario" onClick={() => void exportar('excel')}
            disabled={exportando !== null || total === 0}
            title={total === 0 ? SIN_NADA_QUE_EXPORTAR : undefined}>
            {exportando === 'excel' ? 'Exportando…' : 'Exportar .xlsx'}
          </button>
          <button className="secundario" onClick={() => void exportar('pdf')}
            disabled={exportando !== null || total === 0}
            title={total === 0 ? SIN_NADA_QUE_EXPORTAR : undefined}>
            {exportando === 'pdf' ? 'Exportando…' : 'Exportar PDF'}
          </button>
          <button className="primario" onClick={() => setMostrarAlta(true)}>Alta de cliente</button>
        </span>
      </header>

      <div className="encabezado" style={{ gap: 12, margin: '12px 0' }}>
        <span className="campo-con-icono">
          <IconoBuscar />
          <input
            className="buscador compacta"
            style={{ width: 240, margin: 0 }}
            placeholder="Buscar por nombre o RFC"
            aria-label="Buscar por nombre o RFC"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </span>
        <div className="seg" role="radiogroup" aria-label="Tipo de persona">
          {([['', 'Todos'], ['Moral', 'Morales'], ['Fisica', 'Físicas']] as const).map(([valor, etiqueta]) => (
            <button key={valor || 'todos'} type="button" className="seg-opcion"
              role="radio" aria-checked={tipoPersona === valor}
              onClick={() => setTipoPersona(valor as TipoPersona | '')}>
              {etiqueta}
            </button>
          ))}
        </div>
        <select className="compacta" style={{ width: 'auto', margin: 0 }}
          value={regimenFiscal} onChange={(e) => setRegimenFiscal(e.target.value)} aria-label="Régimen fiscal">
          <option value="">Régimen: todos</option>
          {regimenes.map((r) => (
            <option key={r.clave} value={r.clave}>{r.clave} · {r.descripcion}</option>
          ))}
        </select>
        <select className="compacta" style={{ width: 'auto', margin: 0 }}
          value={estado} onChange={(e) => setEstado(e.target.value as '' | 'Activo' | 'Archivado')} aria-label="Estado">
          <option value="Activo">Estatus: activos</option>
          <option value="Archivado">Estatus: archivados</option>
          <option value="">Estatus: todos</option>
        </select>
      </div>

      {error && <p className="error" role="alert">{error}</p>}
      {errorExportar && <p className="error" role="alert">{errorExportar}</p>}

      {alertaDeAlta?.riesgo69B?.esRiesgoActivo && (
        <p className="aviso-critico" role="alert" style={{ display: 'block' }}>
          <strong>
            {alertaDeAlta.rfc} ({alertaDeAlta.razonSocial}) aparece en el listado 69-B del SAT
          </strong>{' '}
          ({alertaDeAlta.riesgo69B.situacion}). El cliente quedó dado de alta; revisa el caso antes de
          emitir o deducir operaciones con este contribuyente (RF-206).{' '}
          <button className="enlace" onClick={() => setAlertaDeAlta(null)}>Entendido</button>
        </p>
      )}

      {reactivadoDesdeElAlta && (
        <p className="aviso" role="status">
          Reactivaste el cliente {reactivadoDesdeElAlta}. Conserva los datos fiscales que tenía al archivarlo, no
          los que capturaste en el alta: revísalos en su expediente.{' '}
          <button className="enlace" onClick={() => setReactivadoDesdeElAlta(null)}>Entendido</button>
        </p>
      )}

      <div className="tabla-envoltura">
      <table className="tabla">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Régimen</th>
            <th className="num">Periodo abierto</th>
            <th className="num">CFDI pend.</th>
            <th>e.firma</th>
            <th>69-B</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {clientes.map((c) => {
            const resumen = resumenes[c.id]
            const credencial = credenciales[c.id]
            const archivado = c.estado === 'Archivado'
            return (
              <tr key={c.id} className={archivado ? 'inactiva' : undefined}>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.razonSocial}</div>
                  <div className="secundaria" style={{ fontSize: 11 }}>
                    <span className="mono">{c.rfc}</span>
                    {archivado && <span className="insignia neutra" style={{ marginLeft: 6 }}>Archivado</span>}
                  </div>
                </td>
                <td className="secundaria" style={{ whiteSpace: 'nowrap' }} title={c.regimenDescripcion}>
                  {c.tipoPersona === 'Moral' ? 'PM' : 'PF'} · {c.regimenFiscal}
                </td>
                {/* Un cliente archivado no genera trabajo pendiente: sus columnas van vacías en
                    vez de en cero, que se leería como «al corriente». */}
                <td className="num">{archivado ? '—' : (resumen?.periodosAbiertos ?? '—')}</td>
                <td className="num">{archivado ? '—' : (resumen?.cfdiSinContabilizar ?? '—')}</td>
                <td>{archivado ? <span className="secundaria">—</span> : insigniaDeEfirma(credencial)}</td>
                <td>{archivado ? <span className="secundaria">—</span> : insigniaDe69B(resumen)}</td>
                {/* C3 · El prototipo hace clicable la fila entera con un chevron al final. Aquí
                    va un enlace explícito y rotulado en vez de la fila: una fila clicable no la
                    alcanza el teclado ni la anuncia un lector de pantalla. El chevron sí es el
                    del prototipo, que hasta ahora era el carácter «›» suelto mientras
                    `IconoChevron` esperaba en iconos.tsx sin que nadie lo usara. */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {archivado ? (
                    <button className="enlace" onClick={() => void reactivar(c)}>Reactivar</button>
                  ) : (
                    <Link className="enlace" to={`/clientes/${c.id}`} aria-label={`Abrir el expediente de ${c.razonSocial}`}>
                      Abrir <IconoChevron />
                    </Link>
                  )}
                </td>
              </tr>
            )
          })}
          {clientes.length === 0 && (
            <tr>
              <td colSpan={7} className="vacio">
                {busqueda || tipoPersona || regimenFiscal || estado !== 'Activo'
                  ? 'Ningún cliente coincide con este filtro.'
                  : 'Aún no hay clientes en tu cartera.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {clientes.length > 0 && (
        <p className="menuda" style={{ marginTop: 'var(--space-3)' }}>
          Mostrando {clientes.length} de {total}{' '}
          <button className="enlace" disabled={pagina <= 1} onClick={() => setPagina((n) => Math.max(1, n - 1))}>
            ← anterior
          </button>{' '}
          <span className="mono">página {pagina}</span>{' '}
          <button className="enlace" disabled={pagina * TAMANO_DE_PAGINA >= total} onClick={() => setPagina((n) => n + 1)}>
            siguiente →
          </button>
          {' · '}los archivados conservan sus datos por el plazo legal de cinco años.
        </p>
      )}

      {mostrarAlta && (
        <FormularioDeAlta
          onCerrar={() => setMostrarAlta(false)}
          onGuardado={(creado) => {
            setMostrarAlta(false)
            setAlertaDeAlta(creado)
            void recargar()
          }}
          onReactivado={(rfc) => {
            setMostrarAlta(false)
            setReactivadoDesdeElAlta(rfc)
            void recargar()
          }}
        />
      )}

      <PanelDeListado69B />
    </section>
  )
}

function FormularioDeAlta({ onCerrar, onGuardado, onReactivado }: {
  onCerrar: () => void
  onGuardado: (creado: ClienteCreado) => void
  onReactivado: (rfc: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [tipoPersona, setTipoPersona] = useState<TipoPersona>('Moral')
  const [regimenes, setRegimenes] = useState<RegimenFiscal[]>([])
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)
  // CLIENTE_ARCHIVADO: el RFC es de un cliente archivado que quien captura puede reactivar. La API
  // solo manda el id en ese caso. Se guarda con el RFC que lo provocó: si después se corrige el
  // RFC, el botón ya no reactivaría al cliente que el contador tiene escrito.
  const [archivado, setArchivado] = useState<{ id: string; rfc: string } | null>(null)

  // RF-202: la CSF es opcional y solo SUGIERE estos tres campos — el contador los ve, los
  // corrige si hace falta y solo entonces da de alta. Por eso son controlados (para poder
  // prellenarlos) y no auto-envían nada por sí solos.
  const [rfc, setRfc] = useState('')
  const [regimenFiscal, setRegimenFiscal] = useState('')
  const [codigoPostalFiscal, setCodigoPostalFiscal] = useState('')
  const [extrayendoCsf, setExtrayendoCsf] = useState(false)
  const [avisoCsf, setAvisoCsf] = useState<string | null>(null)

  // El catálogo se filtra por tipo de persona: el formulario no ofrece combinaciones inválidas (A-02).
  useEffect(() => {
    api.regimenesFiscales(tipoPersona).then(setRegimenes).catch((e) => setError(e.message))
  }, [tipoPersona])

  // Si el régimen capturado (a mano o sugerido por la CSF) ya no aparece en el catálogo vigente
  // para el tipo de persona elegido, se cae al primero de la lista — igual que hacía el
  // <select> sin controlar de antes al cambiar de tipo de persona.
  useEffect(() => {
    if (!regimenes.some((r) => r.clave === regimenFiscal)) setRegimenFiscal(regimenes[0]?.clave ?? '')
  }, [regimenes, regimenFiscal])

  async function alCambiarArchivoCsf(evento: React.ChangeEvent<HTMLInputElement>) {
    const archivo = evento.target.files?.[0]
    evento.target.value = '' // permite volver a elegir el mismo archivo si hace falta reintentar
    if (!archivo) return

    setAvisoCsf(null)
    setExtrayendoCsf(true)
    try {
      const resultado = await api.extraerCsf(archivo)
      if (resultado.rfc) setRfc(resultado.rfc)
      if (resultado.regimenFiscal) setRegimenFiscal(resultado.regimenFiscal)
      if (resultado.codigoPostal) setCodigoPostalFiscal(resultado.codigoPostal)
      setAvisoCsf(
        resultado.advertencia ??
          'Datos leídos de la constancia. Revísalos antes de guardar: cualquiera se puede corregir a mano.',
      )
    } catch (e) {
      setAvisoCsf(e instanceof Error ? e.message : 'No se pudo leer la constancia.')
    } finally {
      setExtrayendoCsf(false)
    }
  }

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setGuardando(true)

    const datos = new FormData(evento.currentTarget)

    try {
      const creado = await api.crearCliente({
        tipoPersona,
        rfc,
        razonSocial: String(datos.get('razonSocial')),
        regimenFiscal,
        codigoPostalFiscal,
        fechaInicioOperaciones: String(datos.get('fechaInicioOperaciones')),
        correoContacto: String(datos.get('correoContacto') ?? '') || undefined,
      })
      onGuardado(creado)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el cliente.')
      const id = e instanceof ErrorDeApi && e.codigo === 'CLIENTE_ARCHIVADO' ? e.cuerpo?.clienteId : undefined
      setArchivado(typeof id === 'string' ? { id, rfc } : null)
    } finally {
      setGuardando(false)
    }
  }

  async function reactivarArchivado(cliente: { id: string; rfc: string }) {
    setError(null)
    setGuardando(true)
    try {
      await api.reactivarCliente(cliente.id)
      onReactivado(cliente.rfc)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reactivar el cliente.')
      setGuardando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        <h2 id={idTitulo}>Alta de cliente</h2>

        <div className="tarjeta">
          <label>
            Cargar Constancia de Situación Fiscal (opcional)
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => void alCambiarArchivoCsf(e)} />
          </label>
          <p className="nota">
            Lee el PDF y sugiere RFC, régimen fiscal y código postal abajo; revísalos y corrígelos
            antes de guardar (RF-202).
          </p>
          {extrayendoCsf && <p className="nota" role="status">Leyendo la constancia…</p>}
          {avisoCsf && <p className="aviso" role="status">{avisoCsf}</p>}
        </div>

        <label>
          Tipo de persona
          <select value={tipoPersona} onChange={(e) => setTipoPersona(e.target.value as TipoPersona)}>
            <option value="Moral">Persona moral (RFC de 12)</option>
            <option value="Fisica">Persona física (RFC de 13)</option>
          </select>
        </label>

        <label>
          RFC
          <input
            required
            maxLength={13}
            placeholder="ABC010101AB1"
            className="mono"
            value={rfc}
            onChange={(e) => setRfc(e.target.value)}
          />
        </label>

        <label>
          {tipoPersona === 'Moral' ? 'Razón social' : 'Nombre completo'}
          <input name="razonSocial" required maxLength={300} />
        </label>

        <label>
          Régimen fiscal
          <select value={regimenFiscal} onChange={(e) => setRegimenFiscal(e.target.value)} required>
            {regimenes.map((r) => (
              <option key={r.clave} value={r.clave}>
                {r.clave} · {r.descripcion}
              </option>
            ))}
          </select>
        </label>

        <label>
          Código postal fiscal
          <input
            required
            pattern="\d{5}"
            maxLength={5}
            placeholder="80000"
            value={codigoPostalFiscal}
            onChange={(e) => setCodigoPostalFiscal(e.target.value)}
          />
        </label>

        <label>
          Inicio de operaciones en la plataforma
          <input type="date" name="fechaInicioOperaciones" required defaultValue={new Date().toISOString().slice(0, 10)} />
        </label>

        <label>
          Correo de contacto (opcional)
          <input type="email" name="correoContacto" />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>
            Cancelar
          </button>
          {archivado?.rfc === rfc && (
            <button type="button" className="secundario" disabled={guardando} onClick={() => void reactivarArchivado(archivado)}>
              Reactivar el cliente archivado
            </button>
          )}
          <button type="submit" className="primario" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  )
}
